import React from "react";
import { Box, Typography } from "@mui/material";
import { TrendingUp, TrendingDown, AccountBalanceWallet } from "@mui/icons-material";

/**
 * 总览卡片组件 - 支持大、中、小三种布局
 * @param {Object} data - 统计数据 { income, expense, balance }
 * @param {string} size - 布局大小: 'large' | 'medium' | 'small'
 * @param {string} incomeLabel - 收入标签
 * @param {string} expenseLabel - 支出标签
 * @param {string} balanceLabel - 结余标签
 */
export const OverviewCard = ({
    data,
    size = 'large',
    incomeLabel = '收入',
    expenseLabel = '支出',
    balanceLabel = '结余'
}) => {
    if (!data) return null;

    // 根据size获取样式配置
    const getSizeConfig = () => {
        switch (size) {
            case 'small':
                return {
                    containerSx: {
                        flex: 1,
                        minWidth: 100,
                        p: 0.75,
                        borderRadius: 1.5,
                    },
                    iconBoxSx: {
                        p: 0.3,
                        borderRadius: 1,
                    },
                    valueVariant: 'body2',
                    valueSx: { fontSize: '0.85rem', fontWeight: 700 },
                    labelVariant: 'caption',
                    labelSx: { fontSize: '0.65rem', fontWeight: 500 },
                    iconFontSize: 14,
                    horizontal: true,
                };
            case 'medium':
                return {
                    containerSx: {
                        flex: 1,
                        minWidth: 0,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                    },
                    iconBoxSx: {
                        p: 0.5,
                        borderRadius: 1.25,
                        mr: 0.5,
                    },
                    valueVariant: 'h6',
                    valueSx: { fontWeight: 700 },
                    labelVariant: 'h6',
                    labelSx: { fontWeight: 500, whiteSpace: 'nowrap' },
                    iconFontSize: 16,
                    horizontal: true,
                };
            case 'large':
            default:
                return {
                    containerSx: {
                        flex: 1,
                        minWidth: 180,
                        p: 1.75,
                        borderRadius: 2.5,
                        gap: 1,
                    },
                    iconBoxSx: {
                        p: 0.75,
                        borderRadius: 1.5,
                    },
                    valueVariant: 'h5',
                    valueSx: { fontSize: '1.5rem', fontWeight: 700 },
                    labelVariant: 'body2',
                    labelSx: { fontSize: '0.8rem', fontWeight: 500 },
                    iconFontSize: 20,
                };
        }
    };

    const sizeConfig = getSizeConfig();
    const incomeTotal = data.income?.total ?? data.income?.amount ?? 0;
    const expenseTotal = data.expense?.total ?? data.expense?.amount ?? 0;
    const balance = data.balance ?? data.total?.amount ?? (incomeTotal - expenseTotal);

    const items = [
        {
            label: incomeLabel,
            value: `¥${Number(incomeTotal).toLocaleString()}`,
            icon: <TrendingUp sx={{ fontSize: sizeConfig.iconFontSize }} />,
            color: '#ef4444',
            bgColor: '#fef2f2'
        },
        {
            label: expenseLabel,
            value: `¥${Number(expenseTotal).toLocaleString()}`,
            icon: <TrendingDown sx={{ fontSize: sizeConfig.iconFontSize }} />,
            color: '#10b981',
            bgColor: '#ecfdf5'
        },
        {
            label: balanceLabel,
            value: `¥${Number(balance).toLocaleString()}`,
            icon: <AccountBalanceWallet sx={{ fontSize: sizeConfig.iconFontSize }} />,
            color: balance >= 0 ? '#3b82f6' : '#f59e0b',
            bgColor: balance >= 0 ? '#eff6ff' : '#fffbeb'
        }
    ];

    return (
        <Box sx={{ display: 'flex', gap: size === 'large' ? 2 : (size === 'medium' ? 2 : 0.75), flexWrap: size === 'large' ? 'wrap' : 'nowrap' }}>
            {items.map((item, index) => (
                <Box
                    key={index}
                    sx={{
                        ...sizeConfig.containerSx,
                        bgcolor: item.bgColor,
                        transition: 'all 0.3s ease',
                        border: '1px solid transparent',
                        display: 'flex',
                        flexDirection: sizeConfig.horizontal ? 'row' : 'column',
                        alignItems: sizeConfig.horizontal ? 'center' : 'flex-start',
                        justifyContent: sizeConfig.horizontal ? 'space-between' : undefined,
                        gap: sizeConfig.horizontal ? 0.5 : undefined,
                        '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                            borderColor: item.color
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                            sx={{
                                ...sizeConfig.iconBoxSx,
                                bgcolor: item.color,
                                color: 'white',
                                display: 'flex',
                                flexShrink: 0,
                                mr: sizeConfig.horizontal ? 0.5 : 0,
                            }}
                        >
                            {item.icon}
                        </Box>
                        <Typography variant={sizeConfig.labelVariant} sx={{ ...sizeConfig.labelSx, color: 'text.secondary', lineHeight: 1.2 }}>
                            {item.label}
                        </Typography>
                    </Box>
                    <Typography variant={sizeConfig.valueVariant} sx={{ ...sizeConfig.valueSx, color: item.color, lineHeight: 1.2 }}>
                        {item.value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};
